import type { DrawingTool } from "../../canvas/canvasTypes";
import type { StudioTool } from "../../comic/comicTypes";
import type { FillTolerance } from "../../fill/fillTypes";
import type { ShapeMode, ShapeTool } from "../../shapes/shapeTypes";
import type { StudioToolLevel } from "../../parent/parentTypes";
import { BrushOpacityControl } from "../BrushOpacityControl/BrushOpacityControl";
import { BrushSizeControl } from "../BrushSizeControl/BrushSizeControl";
import { ColourPicker } from "../ColourPicker/ColourPicker";

const DRAWING_TOOLS: ReadonlyArray<{ tool: DrawingTool; icon: string; label: string }> = [
  { tool: "pencil", icon: "✎", label: "Pencil" },
  { tool: "marker", icon: "▰", label: "Marker" },
  { tool: "crayon", icon: "✦", label: "Crayon" },
  { tool: "eraser", icon: "◇", label: "Eraser" },
];

const SHAPE_TOOLS: ReadonlyArray<{ tool: ShapeTool; icon: string; label: string }> = [
  { tool: "line", icon: "╱", label: "Line" },
  { tool: "rectangle", icon: "▭", label: "Rectangle" },
  { tool: "ellipse", icon: "◯", label: "Ellipse" },
];

interface ToolDockProps {
  active?: boolean;
  selectionActive?: boolean;
  selectionAvailable?: boolean;
  studioTool: StudioTool;
  tool: DrawingTool;
  colour: string;
  size: number;
  opacity: number;
  shapeMode: ShapeMode;
  fillTolerance: FillTolerance;
  toolLevel?: StudioToolLevel;
  onToolChange: (tool: DrawingTool) => void;
  onStudioToolChange: (tool: StudioTool) => void;
  onColourChange: (colour: string) => void;
  onSizeChange: (size: number) => void;
  onOpacityChange: (opacity: number) => void;
  onShapeModeChange: (mode: ShapeMode) => void;
  onFillToleranceChange: (tolerance: FillTolerance) => void;
  onSelectArt: () => void;
  onFit: () => void;
  onReset: () => void;
}

function toolClass(selected: boolean): string {
  return `tool-button${selected ? " tool-button--selected" : ""}`;
}

export function ToolDock({
  active = true,
  selectionActive = false,
  selectionAvailable = true,
  studioTool,
  tool,
  colour,
  size,
  opacity,
  shapeMode,
  fillTolerance,
  toolLevel = "full",
  onToolChange,
  onStudioToolChange,
  onColourChange,
  onSizeChange,
  onOpacityChange,
  onShapeModeChange,
  onFillToleranceChange,
  onSelectArt,
  onFit,
  onReset,
}: ToolDockProps) {
  const shapeSelected = studioTool === "rectangle" || studioTool === "ellipse";
  return (
    <nav className="tool-dock" aria-label="Drawing controls">
      {toolLevel === "full" ? (
        <div className="tool-dock__group" aria-label="Select Art tool">
          <span className="tool-dock__group-label">Select</span>
          <div className="tool-dock__tools" role="toolbar" aria-label="Select Art tool">
            <button
              aria-label="Select Art"
              aria-pressed={selectionActive}
              className={toolClass(selectionActive)}
              disabled={!selectionAvailable}
              onClick={onSelectArt}
              type="button"
            >
              <span aria-hidden="true">□</span>
              <small>Select Art</small>
            </button>
          </div>
        </div>
      ) : null}

      <div className="tool-dock__group" aria-label="Freehand drawing tools">
        <span className="tool-dock__group-label">Draw</span>
        <div className="tool-dock__tools" role="toolbar" aria-label="Freehand drawing tools">
          {DRAWING_TOOLS.map((item) => (
            <button
              aria-label={item.label}
              aria-pressed={active && tool === item.tool}
              className={toolClass(active && tool === item.tool)}
              disabled={!selectionAvailable}
              key={item.tool}
              onClick={() => onToolChange(item.tool)}
              type="button"
            >
              <span aria-hidden="true">{item.icon}</span>
              <small>{item.label}</small>
            </button>
          ))}
        </div>
      </div>

      {toolLevel !== "basic" ? (
        <div className="tool-dock__group" aria-label="Colour tools">
          <span className="tool-dock__group-label">Colour</span>
          <div className="tool-dock__tools" role="toolbar" aria-label="Colour tools">
            <button
              aria-label="Fill"
              aria-pressed={studioTool === "fill"}
              className={toolClass(studioTool === "fill")}
              disabled={!selectionAvailable}
              onClick={() => onStudioToolChange("fill")}
              type="button"
            >
              <span aria-hidden="true">▧</span>
              <small>Fill</small>
            </button>
            <button
              aria-label="Pick Colour"
              aria-pressed={studioTool === "pickColour"}
              className={toolClass(studioTool === "pickColour")}
              disabled={!selectionAvailable}
              onClick={() => onStudioToolChange("pickColour")}
              type="button"
            >
              <span aria-hidden="true">⌾</span>
              <small>Pick Colour</small>
            </button>
          </div>
          {studioTool === "fill" ? (
            <div className="tool-dock__segments" role="group" aria-label="Fill tolerance">
              {(
                [
                  [0, "Tight"],
                  [24, "Normal"],
                  [48, "Wide"],
                ] as const
              ).map(([value, label]) => (
                <button
                  aria-pressed={fillTolerance === value}
                  key={value}
                  onClick={() => onFillToleranceChange(value)}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {toolLevel !== "basic" ? (
        <div className="tool-dock__group" aria-label="Shape tools">
          <span className="tool-dock__group-label">Shapes</span>
          <div className="tool-dock__tools" role="toolbar" aria-label="Shape tools">
            {SHAPE_TOOLS.map((item) => (
              <button
                aria-label={item.label}
                aria-pressed={studioTool === item.tool}
                className={toolClass(studioTool === item.tool)}
                disabled={!selectionAvailable}
                key={item.tool}
                onClick={() => onStudioToolChange(item.tool)}
                type="button"
              >
                <span aria-hidden="true">{item.icon}</span>
                <small>{item.label}</small>
              </button>
            ))}
          </div>
          {shapeSelected ? (
            <div className="tool-dock__segments" role="group" aria-label="Shape style">
              {(["outline", "solid"] as const).map((mode) => (
                <button
                  aria-pressed={shapeMode === mode}
                  key={mode}
                  onClick={() => onShapeModeChange(mode)}
                  type="button"
                >
                  {mode === "outline" ? "Outline" : "Solid"}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <ColourPicker onChange={onColourChange} value={colour} />
      <div className="tool-dock__sliders">
        <BrushSizeControl onChange={onSizeChange} value={size} />
        <BrushOpacityControl
          disabled={studioTool === "eraser" || studioTool === "pickColour"}
          onChange={onOpacityChange}
          value={opacity}
        />
      </div>
      <div className="tool-dock__view" role="group" aria-label="Canvas view">
        <button className="view-button" onClick={onFit} type="button">
          <span aria-hidden="true">▣</span>
          Fit canvas
        </button>
        <button className="view-button" onClick={onReset} type="button">
          <span aria-hidden="true">100</span>
          Reset zoom
        </button>
      </div>
    </nav>
  );
}
